import { createServerFn } from "@tanstack/react-start";

export type BusinessPublicHead = {
  id: string;
  name: string;
  kind: string | null;
  city: string | null;
  country: string | null;
  description: string | null;
  descriptions: Record<string, string> | null;
  photo_url: string | null;
};

/**
 * Minimal public data for SSR head() on /podnik/$id — name, city, country,
 * kind, first photo — so search engines, social crawlers and TV catalogs
 * see a page-specific title, description and OG image.
 */
export const getBusinessPublicHead = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }): Promise<BusinessPublicHead | null> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    const { data: biz, error } = await supabase
      .from("businesses")
      .select("id, name, kind, status, city, country, description, descriptions")
      .eq("id", data.id)
      .maybeSingle();
    if (error || !biz) return null;

    // Try to grab first photo storage path (best-effort; ignore errors).
    let photo_url: string | null = null;
    try {
      const { data: photos } = await supabase
        .from("business_photos")
        .select("storage_path, created_at")
        .eq("business_id", data.id)
        .order("created_at", { ascending: true })
        .limit(1);
      const path = photos?.[0]?.storage_path as string | undefined;
      if (path) {
        const { data: pub } = supabase.storage
          .from("business-photos")
          .getPublicUrl(path);
        photo_url = pub?.publicUrl ?? null;
      }
    } catch {
      /* ignore */
    }

    return {
      id: biz.id as string,
      name: (biz.name as string) ?? "",
      kind: (biz.kind as string) ?? null,
      city: (biz.city as string) ?? null,
      country: (biz.country as string) ?? null,
      description: (biz.description as string) ?? null,
      descriptions: (biz.descriptions as Record<string, string> | null) ?? null,
      photo_url,
    };
  });
